import { Trans } from '@lingui/macro'
import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  Typography
} from '@mui/material'
import SFAuthService from 'app/services/sfAuth/SFAuthService'
import { FormikCKEditor } from 'app/views/common-components/CKEditor/FormikCKEditor'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { PostalCodeFormat } from 'app/views/common/Formats'
import { useField } from 'formik'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { languages } from 'translation/I18nConnectedProvider'
import { DialogTitleWithIconClose } from 'app/views/common-components/DialogTitleWithIconClose'

/**
 * Form element which renders a Material UI text input.
 * @category Form
 * @subcategory Form elements
 * @component
 * @returns {JSX.Element}
 * @param  {Object} typeProps - Element specific properties that can be configured in form editor.
 * @param  {boolean}  [typeProps.required=false] If providing input to this field should be required in the form.
 * @param  {number} [typeProps.maxChar] How many characters can be input in the field.
 * @param  {boolean}  [typeProps.expandable=false] If text input should expand into new lines on new line insert.
 * @param  {number} [typeProps.rows=1] How many rows the text input should occupy.
 * @param  {boolean}  [typeProps.readOnly=false] If field should be render in read only view.
 * @param  {boolean} [typeProps.isAdvancedTextEditor=false] If text input should be rendered as CKEditor, which utilizies more editing and formatting options.
 * @param  {boolean}  [typeProps.wordCount=false] If field shoud render counter for words input.
 * @param  {boolean} [typeProps.isPostalCode=false] If text input should format expecting postal code input.
 * @param  {boolean}  [typeProps.requiresRequest=false] If value of the field shoud be saved separatly from the rest of the form and ask for permission to send saving request.
 * @param  {number} [typeProps.showFieldLabel=false] If form element title should be rendered as label inside of text field.
 */
export const FormTextField = ({
  id,
  useMultiuser,
  muBag,
  connectedObject,
  disabled,
  preview,
  typeProps,
  langVersion,
  title,
  connectedMap,
  ...props
}) => {
  const {
    required,
    maxChar,
    expandable,
    rows = 1,
    readOnly,
    isAdvancedTextEditor,
    allowAddingTables,
    wordCount,
    isPostalCode,
    isUrl,
    isEmail,
    regexValidationMessage,
    requiresRequest,
    showFieldLabel,
    defaultValue
  } = typeProps
  const { length } = props.connectedFieldDetails || {}
  let limit = maxChar || length
  if (limit > length) {
    if (isAdvancedTextEditor) {
      limit = Math.ceil(length / 2 / 10) * 10
    } else {
      limit = length
    }
  }

  const [dialogOpen, openDialog] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const [field, meta] = useField(id)
  const value = field.value || defaultValue

  if (readOnly) {
    return <Typography>{value || defaultValue}</Typography>
  }

  const baseProps = {}
  if (isPostalCode) {
    baseProps.inputComponent = PostalCodeFormat
  }

  if (isAdvancedTextEditor) {
    return (
      <FormikCKEditor
        name={id}
        disabled={disabled}
        style={{ marginTop: 5 }}
        label={title}
        maxCharacters={limit && Number(limit)}
        muBag={muBag}
        useMultiuser={useMultiuser}
        wordCount={wordCount}
        allowAddingTables={allowAddingTables}
        typeProps={typeProps}
      />
    )
  }

  return (
    <>
      <Grid container wrap='nowrap'>
        <TextFieldWithFormik
          name={id}
          disabled={disabled}
          label={showFieldLabel && title}
          multiline={expandable || rows > 1}
          minRows={rows}
          maxRows={expandable ? 30 : rows}
          required={required}
          limit={limit && Number(limit)}
          wordCount={wordCount}
          type={isUrl ? 'url' : isEmail ? 'email' : 'text'}
          initialValue={value}
          helperArray={regexValidationMessage &&
          [<span key='regex'>
            {regexValidationMessage[langVersion]}
            {'. '}
           </span>]}
        />
        {requiresRequest && (
          <Grid
            item
            style={{
              paddingLeft: 10,
              paddingTop: 15,
              minWidth: 160
            }}
          >
            <Button
              disabled={requesting || !value || meta.error}
              color='primary'
              variant='contained'
              onClick={e => {
                setRequesting(true)
                const conn = SFAuthService.getConnection()
                if (
                  conn &&
                  connectedObject &&
                  Array.isArray(typeProps.connectedTo)
                ) {
                  const sending = enqueueSnackbar(
                    <Trans>Sending request</Trans>,
                    {
                      variant: 'info'
                    }
                  )
                  const promises = typeProps.connectedTo.map(connected => {
                    const { connectedObject, connectedField } = connected
                    let sfObject = connectedMap[connectedObject].sfObject
                    let fieldName = connectedField?.name
                    if (connectedField && fieldName.indexOf('.') !== '-1') {
                      const subObjectName = fieldName.split('.')[0]
                      const subFieldName = fieldName.split('.')[1]
                      if (subObjectName && subFieldName) {
                        fieldName = subFieldName
                        sfObject = sfObject[subObjectName]
                      }
                    }
                    const toUpdate = {
                      Id: sfObject.Id
                    }
                    toUpdate[fieldName] = value
                    return conn
                      .sobject(sfObject.attributes.type)
                      .update(toUpdate)
                  })
                  Promise.all(promises)
                    .then(r => {
                      openDialog(true)
                      setRequesting(false)
                      closeSnackbar(sending)
                    })
                    .catch(err => {
                      closeSnackbar(sending)
                      setRequesting(false)
                      console.error(err)
                    })
                } else {
                  setRequesting(false)
                  enqueueSnackbar(
                    <Trans>There is no field connected to this element!</Trans>,
                    {
                      variant: 'error'
                    }
                  )
                }
              }}
            >
              <Trans>Request change</Trans>
            </Button>
          </Grid>
        )}
      </Grid>
      {requiresRequest && (
        <Dialog open={Boolean(dialogOpen)} fullWidth maxWidth='xs'>
          <DialogTitleWithIconClose 
            label={<Trans>Check your email for confirmation</Trans>}
            handleClose={() => {
                  openDialog(false)
                }}
          />
          <DialogContent sx={{ padding: '0 48px 48px 48px' }}>
            {title && (
              <Typography>
                <Trans>
                  To change {title.toLowerCase()} you have to click confirmation
                  link which was send to you via email
                </Trans>
              </Typography>
            )}
            <Grid container justifyContent='center' sx={{ marginTop: '32px' }}>
              <Button
                fullWidth
                color='primary'
                variant='contained'
                onClick={() => {
                  openDialog(false)
                }}
              >
                <Trans>Ok</Trans>
              </Button>
            </Grid>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export const formTextFieldValueToText = (value, question, object) => {
  const { isAdvancedTextEditor } = question.typeProps
  if (isAdvancedTextEditor || /<\/?[a-z][\s\S]*>/i.test(value)) {
    const h2p = require('html2plaintext')
    const parsed = h2p(value)
    const toRet = languages.reduce((acc, lang) => {
      acc[lang] = parsed
      return acc
    }, {})
    return toRet
  }
  const toRet = languages.reduce((acc, lang) => {
    acc[lang] = value || ''
    return acc
  }, {})
  return toRet
}
